import React, { useCallback, useMemo, useState } from 'react';
import { Box, FormPreloader, RawButton, SearchInput, Text } from '@common-fe/common-fe';
import _ from 'lodash';

import { IPermissionsSection } from '@/modules/role/role.types';

import PermissionGroup from './PermissionsGroup';

export interface Props extends Partial<IPermissionsSection> {
  isLoading?: boolean;
  isSearchLess?: boolean;
  isCopyMode?: boolean;
}
const PermissionsSection: React.FC<Props> = ({
  isCopyMode,
  isLoading = false,
  isSearchLess,
  granted = [],
  revoked = [] }) => {
  const [searchString, setSearchString] = useState('');
  const [isGranted, setIsGranted] = useState(true);

  const [collapsedItems, setCollapsedItems] = useState<string[]>([]);

  const handleChangeCollapsedItem = useCallback((key: string) => {
    if(!collapsedItems.includes(key)) {
      setCollapsedItems([...collapsedItems, key]);
    } else {
      const newList = collapsedItems.filter((id) => id !== key);
      setCollapsedItems(newList); 
    }
  }, [collapsedItems]);
  


  const grantedCount = useMemo(() => {
    return granted?.reduce((result: number, current) => {
      return result + (current?.options?.length || 0);
    }, 0);
  }, [granted]);
  const revokedCount = useMemo(() => {
    return revoked?.reduce((result: number, current) => {
      return result + (current?.options?.length || 0);
    }, 0);
  }, [revoked]);

  const currentGroups = useMemo(() => {
    if(isGranted) {
      return granted || [];
    }
    return revoked || [];
  }, [granted, isGranted, revoked]);

  const filteredGroups = useMemo(()=> {
    if(!searchString || !searchString.trim()) {
      return currentGroups;
    }
    const mappedGroups = currentGroups.map((group) => {
      return  {
        ...group,
        options: group.options.filter((item) =>  {
          return _.toLower(item.name).includes(_.toLower(searchString))
          || _.toLower(item.description).includes(_.toLower(searchString));
        })
      };
    });
    const filteredResult = mappedGroups.filter((group) => !!group.options.length);
    return filteredResult;
  },[currentGroups, searchString]);
  const handleCollapseAll = useCallback(() => {
    setCollapsedItems(currentGroups?.map((group) => group.id) || []);
  }, [currentGroups]);
  // const {} = usePermissionsListQuery();
  return (
    <Box direction="column">
      {!isSearchLess && (
        <Box direction="row" height="40px" justify="between" align="center">
          <Text size="xxlarge" weight={400}>
            Permissions
          </Text>
          <Box width="320px">
            <SearchInput value={searchString} placeholder='Search' onChange={setSearchString} />
          </Box>
        </Box>
      )}
      <Box
        direction="row"
        border={isCopyMode ?{side: 'horizontal', color: 'border1'}: undefined}
        pad={{ vertical: 'm' }}
        margin={{top:  isCopyMode ? 'm': ''}}
      >
        
        <RawButton onClick={() => setIsGranted(true)}>
          <Text
            size="medium"
            weight={400}
            style={{ lineHeight: '20px' }}
            data-testid={`permissions_granted_${grantedCount}`}
            color={isGranted ? 'textBody' : 'textAccent'}
          >
            Granted ({grantedCount || 0})
          </Text>
        </RawButton>
        <Box width="12px"></Box>
        <RawButton onClick={() => setIsGranted(false)}>
          <Text
            size="medium"
            weight={400}
            style={{ lineHeight: '20px' }}
            data-testid={`permissions_revoked_${revokedCount}`}
            color={isGranted ? 'textAccent' : 'textBody'}
          >
            Revoked ({revokedCount || 0})
          </Text>
        </RawButton>
      </Box>
      <Box direction='column'>
        <Box pad={{vertical: 'm'}}>
          <RawButton onClick={handleCollapseAll}>
            <Text color="textAccent" weight={400}>
              Collapse all
            </Text>
          </RawButton>
        </Box>
        {isLoading ? (
          <FormPreloader count={4} />
        ) : (
          <Box direction="column">
            {filteredGroups?.map((item) => (
              <PermissionGroup 
                isCopyMode={isCopyMode}
                key={item.id}
                isGranted={isGranted}
                collapsed={collapsedItems.includes(item.id)} 
                onChangeCollapsed={handleChangeCollapsedItem}
                {...item}             
              />
            ))}
            <Box direction="row" height="40px" align="center"></Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PermissionsSection;
